// R45: Eén gedeelde IntersectionObserver. Bij eerste keer in beeld → prefetch.
// R63: debounce op intersect zodat snelle scroll niet 50 calls tegelijk
// afvuurt richting Lightspeed (rate-limit-bescherming).
// R71: debounce 200ms -> 1000ms en max 1 prefetch-request tegelijk, zodat de
// viewport-prefetch het Lightspeed-rate-limit-budget niet opbrandt tijdens printen.
import { useEffect, useRef } from "react";
import { prefetchOrderLines } from "@/lib/orderLinesPrefetch";

let observer: IntersectionObserver | null = null;
const elToOrderid = new WeakMap<Element, number>();
const pendingTimers = new WeakMap<Element, ReturnType<typeof setTimeout>>();

const PREFETCH_DEBOUNCE_MS = 1000;

// R71: hoogstens één prefetch tegelijk. Volgende elementen blijven geobserveerd
// en proberen opnieuw zodra de lopende prefetch klaar is.
let inFlight = false;

function runPrefetch(id: number, target: Element) {
  if (inFlight) return false;
  inFlight = true;
  console.log(`[ui-prefetch R71 orderid=${id}] viewport-trigger`);
  Promise.resolve(prefetchOrderLines(id))
    .catch(() => { /* noop */ })
    .finally(() => { inFlight = false; });
  try { observer?.unobserve(target); } catch { /* noop */ }
  return true;
}

function getObserver() {
  if (observer) return observer;
  if (typeof IntersectionObserver === "undefined") return null;
  observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const id = elToOrderid.get(entry.target);
        if (id == null) continue;

        if (!entry.isIntersecting) {
          // Cancel pending timer if user scrolled away within debounce window.
          const t = pendingTimers.get(entry.target);
          if (t) {
            clearTimeout(t);
            pendingTimers.delete(entry.target);
          }
          continue;
        }

        if (pendingTimers.has(entry.target)) continue;
        const timer = setTimeout(function attempt() {
          pendingTimers.delete(entry.target);
          if (!runPrefetch(id, entry.target)) {
            // Er loopt al een prefetch: opnieuw inplannen i.p.v. parallel vuren.
            const retry = setTimeout(attempt, PREFETCH_DEBOUNCE_MS);
            pendingTimers.set(entry.target, retry);
          }
        }, PREFETCH_DEBOUNCE_MS);
        pendingTimers.set(entry.target, timer);
      }
    },
    { rootMargin: "200px 0px", threshold: 0.01 },
  );
  return observer;
}

export function usePrefetchOnVisible(orderid: number | null) {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || orderid == null) return;
    const obs = getObserver();
    if (!obs) {
      console.log(`[ui-prefetch R71 orderid=${orderid}] viewport-trigger (no-IO fallback)`);
      prefetchOrderLines(orderid);
      return;
    }
    elToOrderid.set(el, orderid);
    obs.observe(el);
    return () => {
      const t = pendingTimers.get(el);
      if (t) {
        clearTimeout(t);
        pendingTimers.delete(el);
      }
      try { obs.unobserve(el); } catch { /* noop */ }
    };
  }, [orderid]);
  return ref;
}
